/**
 * Simple Logo Override System
 * Handles logo replacement across collection, product, cart, and checkout pages
 */
class LogoOverride {
  constructor() {
    this.storageKey = 'logo_override';
    this.logoSelector = '#header-logo'; // Adjust this selector to match your theme
    this.init();
  }

  init() {
    let logoApplied = false;
    
    // Check for collection logo override first
    if (window.collectionLogoOverride) {
      this.detectCollectionLogo();
      logoApplied = true;
    } else {
      // Apply any stored logo override
      const stored = this.getStoredLogo();
      if (stored) {
        this.applyStoredLogo();
        logoApplied = true;
      }
    }
    
    // Always show logo (whether default or overridden)
    this.showLogo();
    
    // Set up cart interception to persist logo data
    this.setupCartInterception();
  }

  /**
   * Check if current page has a collection with logo override
   */
  detectCollectionLogo() {
    // This will be set by Liquid in the header when on a collection page
    console.log('Checking for collection logo override...');
    console.log('window.collectionLogoOverride:', window.collectionLogoOverride);
    
    if (window.collectionLogoOverride) {
      console.log('Collection logo override detected:', window.collectionLogoOverride);
      
      // Always update stored data with current collection's branding
      // This ensures the most recent collection visited determines the cart attributes
      this.storeLogo(window.collectionLogoOverride);
      this.applyLogo(window.collectionLogoOverride);
      
      console.log('✅ Current collection branding will be used for cart attributes');
    } else {
      console.log('No collection logo override found');
    }
  }

  /**
   * Apply logo from localStorage if it exists
   */
  applyStoredLogo() {
    const stored = this.getStoredLogo();
    console.log('Stored logo data:', stored);
    if (stored) {
      console.log('Applying stored logo override:', stored);
      this.applyLogo(stored);
    } else {
      console.log('No stored logo found or data is invalid');
    }
  }

  /**
   * Apply logo to the page
   */
  applyLogo(logoData) {
    console.log('applyLogo called with:', logoData);
    const logoElement = document.querySelector(this.logoSelector);
    console.log('Logo element found:', logoElement);
    
    if (!logoElement) {
      console.error('Logo element not found with selector:', this.logoSelector);
      return;
    }
    
    if (!logoData || !logoData.url) {
      console.error('Invalid logo data or missing URL:', logoData);
      return;
    }
    
    logoElement.src = logoData.url;
    // Clear srcset to ensure our new src is used
    logoElement.srcset = '';
    if (logoData.alt) {
      logoElement.alt = logoData.alt;
    }
    console.log('Logo applied successfully (src and srcset updated):', logoData.url);
  }

  /**
   * Show logo after determining which one to use
   */
  showLogo() {
    const logoElement = document.querySelector(this.logoSelector);
    if (logoElement) {
      logoElement.classList.add('logo-ready');
      console.log('Logo shown with logo-ready class');
    }
  }

  /**
   * Store logo data in localStorage
   */
  storeLogo(logoData) {
    const data = {
      url: logoData.url,
      alt: logoData.alt || 'Logo',
      brand_name: logoData.brand_name || '',
      customer_service_email: logoData.customer_service_email || '',
      website: logoData.website || '',
      accent_color: logoData.accent_color || '',
      privacy_policy_url: logoData.privacy_policy_url || '',
      terms_of_service_url: logoData.terms_of_service_url || '',
      refund_policy_url: logoData.refund_policy_url || '',
      shipping_policy_url: logoData.shipping_policy_url || '',
      display_footer: logoData.display_footer !== undefined ? logoData.display_footer : true,
      timestamp: Date.now()
    };
    localStorage.setItem(this.storageKey, JSON.stringify(data));
    console.log('Logo stored in localStorage');
  }

  /**
   * Get logo data from localStorage
   */
  getStoredLogo() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        // Check if data is less than 24 hours old
        const isExpired = (Date.now() - data.timestamp) > (24 * 60 * 60 * 1000);
        if (isExpired) {
          this.clearStoredLogo();
          return null;
        }
        return data;
      }
    } catch (e) {
      console.error('Error reading stored logo:', e);
    }
    return null;
  }

  /**
   * Clear stored logo data
   */
  clearStoredLogo() {
    localStorage.removeItem(this.storageKey);
    console.log('Stored logo cleared');
  }

  /**
   * Set up cart interception to add logo data to cart attributes
   */
  setupCartInterception() {
    const originalFetch = window.fetch;
    
    window.fetch = (url, options = {}) => {
      // Intercept cart add requests
      if (url.includes('/cart/add')) {
        // Priority: Current collection override > Stored logo data
        const currentCollectionLogo = window.collectionLogoOverride;
        const storedLogo = this.getStoredLogo();
        
        // Use current collection data if available, otherwise fall back to stored
        const logoToUse = currentCollectionLogo || storedLogo;
        
        if (logoToUse) {
          console.log('Intercepting cart add - adding logo data');
          console.log('Using logo source:', currentCollectionLogo ? 'current collection' : 'stored data');
          
          // After successful cart add, update cart attributes with white label data
          // Note: We only use cart attributes, not line item properties, since 
          // email templates access order.attributes (not line_item.properties)
          return originalFetch(url, options).then(response => {
            if (response.ok) {
              this.updateCartAttributes(logoToUse);
            }
            return response;
          });
        }
      }
      
      return originalFetch(url, options);
    };
  }

  /**
   * Update cart attributes with logo data
   */
  updateCartAttributes(logoData) {
    console.log('Updating cart attributes with logo data');
    
    const cartAttributes = {
      'logo_override_url': logoData.url,
      'logo_override_alt': logoData.alt
    };
    
    // Add extended white label attributes if they exist
    if (logoData.brand_name) cartAttributes['white_label_brand_name'] = logoData.brand_name;
    if (logoData.customer_service_email) cartAttributes['white_label_customer_service_email'] = logoData.customer_service_email;
    if (logoData.website) cartAttributes['white_label_website'] = logoData.website;
    if (logoData.accent_color) cartAttributes['white_label_accent_color'] = logoData.accent_color;
    if (logoData.privacy_policy_url) cartAttributes['white_label_privacy_policy_url'] = logoData.privacy_policy_url;
    if (logoData.terms_of_service_url) cartAttributes['white_label_terms_of_service_url'] = logoData.terms_of_service_url;
    if (logoData.refund_policy_url) cartAttributes['white_label_refund_policy_url'] = logoData.refund_policy_url;
    if (logoData.shipping_policy_url) cartAttributes['white_label_shipping_policy_url'] = logoData.shipping_policy_url;
    if (logoData.display_footer !== undefined) cartAttributes['white_label_display_footer'] = logoData.display_footer;
    
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: cartAttributes
      })
    }).catch(error => {
      console.error('Failed to update cart attributes:', error);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new LogoOverride());
} else {
  new LogoOverride();
}
